// TMOS Admin Dashboard Page
// Date: December 9, 2025
// Purpose: Aggregates all admin modules in a dashboard view
//          Provides quick access to all administration functionality

import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '@/state/language/LanguageProvider';
import { useAuth } from '@/state/auth/AuthProvider';
import { unifiedRBACService } from '@/services/unifiedRBACService';
import { iconService } from '@/services/iconService';
import { CogIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';

export const AdminDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user } = useAuth();
  const [adminModules, setAdminModules] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAdminModules = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const adminTree = await unifiedRBACService.getCurrentUserNavigation('ADMIN');
        
        // Filter modules that have at least one screen with read permission
        const modules = adminTree.modules
          .map((module: any) => ({
            ...module,
            children: module.children.filter((child: any) => 
              child.has_read_permission === true
            )
          }))
          .filter((module: any) => module.children.length > 0);
        
        setAdminModules(modules);
      } catch (error) {
        console.error('Error loading admin modules:', error);
      } finally {
        setLoading(false);
      }
    };

    loadAdminModules();
  }, [user?.id]);

  const getTranslatedText = (displayName: string, translationKey?: string): string => {
    if (translationKey) {
      const translated = t(translationKey);
      return translated !== translationKey ? translated : displayName;
    }
    return displayName;
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center text-[var(--color-primary-text)]">
          {t('navigation.loading') || 'Loading...'}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center space-x-2 text-[var(--color-primary-text)] hover:text-[var(--color-secondary-text)] mb-4"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          <span>{t('navigation.backToDashboard') || 'Back to Dashboard'}</span>
        </button>
        
        <div className="flex items-center space-x-3">
          <CogIcon className="w-8 h-8 text-[var(--color-primary-text)]" />
          <div>
            <h1 className="text-3xl font-bold text-[var(--color-primary-text)]">
              {t('admin.dashboard.title') || 'Administration Dashboard'}
            </h1>
            <p className="text-[var(--color-secondary-text)] mt-1">
              {t('admin.dashboard.subtitle') || 'Manage all system administration functions'}
            </p>
          </div>
        </div>
      </div>

      {/* Admin Modules Grid */}
      {adminModules.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {adminModules.map((module: any) => {
            const modulePath = `/modules/${module.module_code.toLowerCase().replace(/_/g, '-')}`;
            return (
              <Link
                key={module.id}
                to={modulePath}
                className="border border-[var(--color-border)] rounded-lg p-6 hover:border-[var(--color-accent-bg)] transition-colors"
              >
                <div className="flex items-start space-x-4">
                  {module.icon && iconService.isIconAvailable(module.icon) ? (
                    React.createElement(iconService.getIcon(module.icon), { 
                      className: 'w-10 h-10 text-[var(--color-primary-text)] flex-shrink-0' 
                    })
                  ) : (
                    <div className="w-10 h-10 bg-[var(--color-secondary-bg)] rounded flex items-center justify-center flex-shrink-0">
                      <CogIcon className="w-6 h-6 text-[var(--color-secondary-text)]" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-[var(--color-primary-text)] mb-1">
                      {getTranslatedText(module.display_name, module.translation_key)}
                    </h3>
                    {module.description && (
                      <p className="text-sm text-[var(--color-secondary-text)] mb-3 line-clamp-2">
                        {module.description}
                      </p>
                    )}
                    <div className="flex items-center space-x-2 text-xs text-[var(--color-secondary-text)]">
                      <span>{module.children.length}</span>
                      <span>{t('admin.dashboard.screens') || 'screens'}</span>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      ) : (
        <div className="border border-[var(--color-border)] rounded-lg p-6">
          <p className="text-[var(--color-surface-text)]">
            {t('admin.dashboard.noModules') || 'No administration modules available.'}
          </p>
        </div>
      )}
    </div>
  );
};

